"use client";

import { format } from "date-fns";
import { useTranslations } from "next-intl";
import { Rating } from "@/components/common/rating";
import { ListingItemAttributes } from "@/components/listing/listing-item-attributes";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { ListingItemHeader } from "@/components/listing/listing-item-header";
import { ListingItemImage } from "@/components/listing/listing-item-image";
import { Pill } from "@/components/pill";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getDateLocale } from "@/features/cart/utils/dates";
import { BadgePromoted } from "@/features/diets/components/badges/diet-badge-promoted";
import type { Globals } from "@/features/globals/types/globals.type";
import { getLocaleForPathname, getPathname, Link } from "@/i18n/navigation";
import { formatCateringName } from "@/lib/helpers/format-brand-name";
import { useMobile } from "@/lib/hooks/use-mobile";
import { querify } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { CateringCatalog } from "../types/catering-catalog.type";
import { CateringDietListSmall } from "./catering-diet-list-small";

type CatalogListItemProps = {
  catering: CateringCatalog;
  globals?: Globals | null;
  searchParams: Record<string, string | string[]>;
  index?: number;
};

export const CateringListItem = ({
  catering,
  globals,
  searchParams,
  index,
}: CatalogListItemProps) => {
  const t = useTranslations("caterings");
  const locale = getLocaleForPathname();
  const dateLocale = getDateLocale(locale);
  const { isMobile } = useMobile();

  const { search, page, ...filteredSearchParams } = searchParams ?? {};

  const cateringName = formatCateringName(catering.name);

  const cateringUrl = querify(
    getPathname({
      href: {
        pathname: "/caterings/[slug]",
        params: { slug: catering.slug },
      },
      locale,
    }),
    { ...(filteredSearchParams as Record<string, string>) },
  );

  return (
    <div>
      <Card className="relative isolate flex w-full flex-col rounded-lg rounded-b-none border-brand-grey2 border-b-0 shadow-none md:flex-row md:border-b md:shadow-[0px_4px_16px_0px_hsla(0,0%,0%,0.08)]">
        <div className="relative flex h-auto">
          <BadgePromoted isPromoted={catering.isPromoted} />
          <ListingItemImage
            className="max-md:h-full! md:h-52 md:w-52 xl:h-60 xl:w-60"
            imageClassName="md:rounded-bl-none"
            imageUrl={catering.heroImageUrl}
            name={cateringName}
            prioritized={typeof index === "number" && index < 2}
          />
        </div>
        <CardContent className="flex flex-1 flex-col px-4 pt-4 pb-2 md:py-4 xl:py-6">
          <div className="flex gap-4">
            <ListingItemCateringImage
              className="absolute top-2 left-2 md:static"
              imageUrl={catering.imageUrl}
              name={cateringName}
            />
            <div className="flex w-0 grow flex-col gap-1">
              <div className="order-2 flex justify-between md:order-1">
                <ListingItemHeader
                  namePrimary={cateringName}
                  urlPrimary={cateringUrl}
                />
              </div>
              {globals?.ratingCateringListing && (
                <div className="flex gap-x-2">
                  <Rating
                    count={catering.rating.count}
                    details={false}
                    isTrending={catering.rating.isTrending}
                    productId={catering.id}
                    productName={cateringName}
                    rating={catering.rating.value}
                    size="md"
                  />
                </div>
              )}
            </div>
          </div>
          {catering.description && (
            <div
              className="mb-6 line-clamp-2 text-sm md:mt-3 md:mb-3 xl:mb-5"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{ __html: catering.description }}
            />
          )}
          <div className="mt-auto flex flex-wrap items-center justify-between gap-6 md:gap-2 xl:gap-6">
            <div className="flex basis-full gap-2 lg:gap-0 xl:basis-1/2">
              {catering.firstPossibleOrderDay && (
                <ListingItemAttributes
                  className="block flex-1 items-end gap-2 md:flex xl:block"
                  label={t("available_on")}
                >
                  <span className="capitalize">
                    {format(new Date(catering.firstPossibleOrderDay), "eeee", {
                      locale: dateLocale,
                    })}
                  </span>
                </ListingItemAttributes>
              )}
            </div>
            <div className="flex basis-full gap-2 xl:basis-auto">
              <div className="flex-1">
                <Link
                  className={cn(
                    buttonVariants({
                      variant: isMobile ? "secondary" : "outline",
                    }),
                    "h-12 min-h-0 rounded-lg border-brand-green2-300 px-5 font-base font-medium max-md:w-full md:h-10 md:text-brand-green2-300 xl:h-12",
                  )}
                  data-pw="check-offer-button"
                  href={{
                    pathname: "/caterings/[slug]",
                    params: { slug: catering.slug },
                  }}
                  locale={locale}
                  title={cateringName}
                >
                  <span className="hidden md:block">{t("check_offer")}</span>
                  <span className="block md:hidden">
                    {t("check_full_offer")}
                  </span>
                  <Pill
                    variant="white"
                    className="ml-2 rounded-md font-bold text-base md:hidden"
                  >
                    {catering.dietsCount}
                  </Pill>
                </Link>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <CateringDietListSmall catering={catering} globals={globals} />
    </div>
  );
};
